/** Numbers and times, spelled the way the design spells them.
 *
 * Every figure in this portal sits in a column that is read downwards — lap
 * times against each other, deltas against zero, costs against last month —
 * so these functions are written for comparison rather than for prose. Digits
 * are always padded to the same count, the thousands separator is a
 * non-breaking space (so "3 912 m" never wraps between the 3 and the 912),
 * and nothing goes through `toLocaleString`, whose output changes with the
 * visitor's locale and would make two rows of the same table disagree about
 * where the decimal point goes.
 *
 * A value that does not exist is an em dash, never "0" and never "null": a
 * lap with no time recorded and a lap driven in no time are different facts.
 */

/** What is printed where a value is missing. */
export const DASH = '—';

/** U+00A0. Groups digits without offering a line break. */
const GROUP = ' ';

/** U+2212. A minus sign is as wide as a plus; a hyphen is not. */
const MINUS = '−';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

function isNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  return Number.isFinite(at) ? new Date(at) : null;
}

// ── lap times ─────────────────────────────────────────────────────────────

/** `1:38.220` — minutes, seconds padded to two, milliseconds to three. Under
 *  a minute the minute is still shown (`0:58.104`) so the column stays
 *  aligned; a lap that broke the minute and one that did not should not
 *  jump. */
export function lapTime(ms: number | null | undefined): string {
  if (!isNumber(ms)) return DASH;
  const sign = ms < 0 ? MINUS : '';
  const total = Math.abs(Math.round(ms));
  const minutes = Math.floor(total / 60_000);
  const seconds = Math.floor((total % 60_000) / 1000);
  const millis = total % 1000;
  return `${sign}${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

/** The same in seconds, for a caller holding the logger's float. */
export function lapTimeSeconds(seconds: number | null | undefined): string {
  return isNumber(seconds) ? lapTime(seconds * 1000) : DASH;
}

/** `38.220` — a sector, which never reaches a minute and does not pretend to. */
export function sectorTime(ms: number | null | undefined): string {
  if (!isNumber(ms)) return DASH;
  return (Math.abs(ms) / 1000).toFixed(3);
}

// ── deltas ────────────────────────────────────────────────────────────────

/** `+0.312` / `−0.312` / `0.000`, always signed, always three decimals. */
export function delta(ms: number | null | undefined, digits = 3): string {
  if (!isNumber(ms)) return DASH;
  const seconds = Math.abs(ms) / 1000;
  const sign = ms > 0 ? '+' : ms < 0 ? MINUS : '';
  return `${sign}${seconds.toFixed(digits)}`;
}

/** The same from a delta already in seconds. */
export function deltaSeconds(seconds: number | null | undefined, digits = 3): string {
  return isNumber(seconds) ? delta(seconds * 1000, digits) : DASH;
}

/** Which semantic colour a delta wears: quicker than the reference is `ok`,
 *  slower is `warn`, and anything inside the epsilon is neither — a five
 *  thousandth of a second is noise, not a finding. */
export function deltaTone(
  ms: number | null | undefined,
  epsilonMs = 5,
): 'ok' | 'warn' | 'level' | 'none' {
  if (!isNumber(ms)) return 'none';
  if (ms > epsilonMs) return 'warn';
  if (ms < -epsilonMs) return 'ok';
  return 'level';
}

// ── times and dates ───────────────────────────────────────────────────────

/** `2 h ago`, `4 min ago`, `2 days ago`, and an absolute date once the
 *  relative one stops meaning anything. Past and future both read naturally,
 *  which matters for a retention date that has not arrived yet. */
export function relativeTime(iso: string | null | undefined, from: Date = new Date()): string {
  const at = parse(iso);
  if (!at) return DASH;

  const deltaMs = from.getTime() - at.getTime();
  const ago = deltaMs >= 0;
  const seconds = Math.abs(deltaMs) / 1000;

  const say = (text: string): string => (ago ? `${text} ago` : `in ${text}`);

  if (seconds < 45) return ago ? 'just now' : 'in a moment';
  if (seconds < 90) return say('1 min');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return say(`${minutes} min`);
  const hours = Math.round(seconds / 3600);
  if (hours < 24) return say(`${hours}${GROUP}h`);
  const days = Math.round(seconds / 86_400);
  if (days < 14) return say(days === 1 ? '1 day' : `${days} days`);
  // Past a fortnight, "31 days ago" is a worse answer than the date itself.
  return shortDate(iso);
}

/** `2 Aug 2026`. */
export function shortDate(iso: string | null | undefined): string {
  const at = parse(iso);
  if (!at) return DASH;
  return `${at.getDate()}${GROUP}${MONTHS[at.getMonth()] ?? ''}${GROUP}${at.getFullYear()}`;
}

/** `14:02`, in the reader's own timezone — a lap was driven at a wall-clock
 *  time, whatever the database stored it as. */
export function clockTime(iso: string | null | undefined): string {
  const at = parse(iso);
  if (!at) return DASH;
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
}

/** `2 Aug 2026 14:02`. */
export function dateTime(iso: string | null | undefined): string {
  const at = parse(iso);
  if (!at) return DASH;
  return `${shortDate(iso)} ${clockTime(iso)}`;
}

/** `Today 20:14`, `Yesterday 21:40`, `13 Sep 19:02` — how a session is
 *  labelled in the list, where the day matters more than the year. */
export function sessionWhen(iso: string | null | undefined, from: Date = new Date()): string {
  const at = parse(iso);
  if (!at) return DASH;
  const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(from) - startOfDay(at)) / 86_400_000);
  if (days === 0) return `Today ${clockTime(iso)}`;
  if (days === 1) return `Yesterday ${clockTime(iso)}`;
  if (at.getFullYear() === from.getFullYear()) {
    return `${at.getDate()}${GROUP}${MONTHS[at.getMonth()] ?? ''} ${clockTime(iso)}`;
  }
  return dateTime(iso);
}

/** `12 s`, `1 m 04 s`, `2 h 13 m` — how long a job run took. */
export function duration(ms: number | null | undefined): string {
  if (!isNumber(ms)) return DASH;
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}${GROUP}s`;
  if (total < 3600) {
    return `${Math.floor(total / 60)}${GROUP}m ${String(total % 60).padStart(2, '0')}${GROUP}s`;
  }
  const hours = Math.floor(total / 3600);
  return `${hours}${GROUP}h ${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}${GROUP}m`;
}

// ── plain numbers ─────────────────────────────────────────────────────────

/** `38 200`. Grouped by hand, because `toLocaleString` groups differently in
 *  different places and these are read against each other. */
export function count(value: number | null | undefined, digits = 0): string {
  if (!isNumber(value)) return DASH;
  const sign = value < 0 ? MINUS : '';
  const fixed = Math.abs(value).toFixed(digits);
  const [whole = '0', fraction] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP);
  return sign + (fraction ? `${grouped}.${fraction}` : grouped);
}

/** `3 912 m`, `20 832 m`. Metres all the way up: a circuit's length is
 *  compared against other circuits', and kilometres would break the column. */
export function metres(value: number | null | undefined): string {
  return isNumber(value) ? `${count(Math.round(value))}${GROUP}m` : DASH;
}

/** `97.4%`, `100%` — a trailing `.0` is noise, and 100 is the number people
 *  are looking for. */
export function percent(value: number | null | undefined, digits = 1): string {
  if (!isNumber(value)) return DASH;
  const fixed = value.toFixed(digits);
  return `${fixed.replace(/\.0+$/, '')}%`;
}

/** `9.9 GB`, `64 MB`, `312 B`. Decimal units, because that is what the
 *  Cloudflare dashboard bills in and the costs page has to agree with it. */
export function bytes(value: number | null | undefined): string {
  if (!isNumber(value)) return DASH;
  const units = ['B', 'kB', 'MB', 'GB', 'TB'] as const;
  let size = Math.abs(value);
  let unit = 0;
  while (size >= 1000 && unit < units.length - 1) {
    size /= 1000;
    unit += 1;
  }
  const digits = unit === 0 ? 0 : size < 10 ? 1 : size < 100 ? 1 : 0;
  const sign = value < 0 ? MINUS : '';
  return `${sign}${size.toFixed(digits)}${GROUP}${units[unit]}`;
}

/** `4 Hz`. */
export function hz(value: number | null | undefined): string {
  if (!isNumber(value)) return DASH;
  const digits = Number.isInteger(value) ? 0 : 1;
  return `${value.toFixed(digits)}${GROUP}Hz`;
}

/** `$3.41`. Two decimals always, because the costs page adds them up. */
export function money(value: number | null | undefined, currency = 'USD'): string {
  if (!isNumber(value)) return DASH;
  const symbol = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '';
  const sign = value < 0 ? MINUS : '';
  const body = `${symbol}${Math.abs(value).toFixed(2)}`;
  return symbol ? sign + body : `${sign}${Math.abs(value).toFixed(2)}${GROUP}${currency}`;
}

/** `+12%` / `−4%` — a change against a previous figure. */
export function changePercent(value: number | null | undefined, digits = 0): string {
  if (!isNumber(value)) return DASH;
  const sign = value > 0 ? '+' : value < 0 ? MINUS : '';
  return `${sign}${Math.abs(value).toFixed(digits)}%`;
}

// ── words ─────────────────────────────────────────────────────────────────

/** `KD` from `kyoto_drift`, `MH` from `m hoshino`, `GT` from `gt7_bot_44`.
 *  Two letters at most, upper case, and never empty for a non-empty name. */
export function initials(name: string | null | undefined): string {
  const text = (name ?? '').trim();
  if (!text) return '?';
  const words = text.split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) {
    return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase();
  }
  return text.slice(0, 2).toUpperCase();
}

/** `1 lap` / `12 laps`, without a helper per noun at every call site. */
export function plural(value: number, one: string, many = `${one}s`): string {
  return `${count(value)} ${Math.abs(value) === 1 ? one : many}`;
}

/** `tracks:write` and friends, as the logger's status line writes them. */
export function scopeList(scopes: readonly string[] | null | undefined): string {
  return scopes && scopes.length ? scopes.join(', ') : 'none';
}
